"use client";

import React from "react";
import { Moon, Loader, Sparkles } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function JoinMagicForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 p-8 text-center animate-in fade-in duration-500">
        <div className="mb-2 flex size-12 items-center justify-center rounded-full bg-primary/20">
          <Sparkles className="size-6 text-primary" />
        </div>
        <h3 className="font-serif text-2xl text-white">
          ¡Bienvenido al Aquelarre!
        </h3>
        <p className="max-w-md text-white/75">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-white/20 bg-white/5 text-white hover:bg-white/10">
          Volver
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="mx-auto w-full max-w-xl space-y-6 text-left">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90">Nombre Completo</FormLabel>
              <FormControl>
                <Input placeholder="Ejemplo: Ana Martínez" {...field} className="border-white/15 bg-white/5 text-white placeholder:text-white/45 focus-visible:border-primary focus-visible:ring-primary/30" />
              </FormControl>
              <FormDescription className="text-xs text-white/55">
                Ingresa tu nombre tal como deseas que te llamemos.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90">
                Correo Electrónico
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="ejemplo@dominio.com" {...field} className="border-white/15 bg-white/5 text-white placeholder:text-white/45 focus-visible:border-primary focus-visible:ring-primary/30" />
              </FormControl>
              <FormDescription className="text-xs text-white/55">
                Usaremos este correo para enviarte novedades y guías de uso.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90">
                Teléfono (opcional)
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+34 600 123 456" {...field} className="border-white/15 bg-white/5 text-white placeholder:text-white/45 focus-visible:border-primary focus-visible:ring-primary/30" />
              </FormControl>
              <FormDescription className="text-xs text-white/55">
                Si deseas una asesoría personalizada, déjanos tu número.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="interest" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90">
                ¿Qué tipo de piedras te interesan?
              </FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full min-w-0 appearance-none rounded-md border border-white/15 bg-white/5 px-3 py-1 text-base text-white shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                  <option value="" disabled className="bg-[#101522] text-white/60">
                    Selecciona una opción
                  </option>
                  <option value="Amatista" className="bg-[#101522] text-white">
                    Amatista
                  </option>
                  <option value="Cuarzo Rosa" className="bg-[#101522] text-white">
                    Cuarzo Rosa
                  </option>
                  <option value="Citrino" className="bg-[#101522] text-white">
                    Citrino
                  </option>
                  <option value="Jade" className="bg-[#101522] text-white">
                    Jade
                  </option>
                  <option value="Obsidiana" className="bg-[#101522] text-white">
                    Obsidiana
                  </option>
                  <option value="Otro" className="bg-[#101522] text-white">
                    Otro
                  </option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-center">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="flex flex-col items-center pt-4">
        <Button type="submit" size="lg" disabled={isSubmitting} className="group min-w-[200px] bg-primary text-primary-foreground transition-all duration-300 hover:bg-primary/90">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Conectando...
            </> : <>
              Suscribirse
              <Moon className="ml-2 size-4 transition-transform group-hover:rotate-12" />
            </>}
        </Button>
        <p className="mt-4 text-sm italic text-white/55">
          Rituales semanales y actualizaciones del ciclo lunar.
        </p>
      </div>
    </Form>;
}
export default function UneteALaMagia() {
  return <section id="únete-a-la-magia" className="relative flex min-h-[80vh] flex-col items-center justify-center overflow-hidden bg-[#070B14] px-4 py-24 text-white">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-chart-2/10 blur-[100px]" />

      <div className="relative z-10 mx-auto w-full max-w-4xl text-center">
        <div className="mb-10 flex flex-col items-center">
          <Separator className="mb-8 h-[2px] w-16 bg-primary" />
          <h2 className="mb-4 font-serif text-4xl font-medium tracking-tight text-white md:text-5xl lg:text-6xl">
            Únete a la Magia
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-white/75">
            Forma parte de nuestra comunidad y recibe sabiduría ancestral, guías
            de cristales y acceso anticipado a nuestras colecciones más
            exclusivas.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur-sm md:p-12">
          <FormProvider formId="join_magic_form_home" sectionName="unete-a-la-magia" defaultValues={{
          name: "",
          email: "",
          phone: "",
          interest: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          }
        }} submitText="Suscribirse" successMessage="Tu energía se ha unido a la nuestra. Pronto recibirás noticias mágicas en tu correo." errorMessage="Las energías están revueltas. Por favor, intenta suscribirte de nuevo.">
            <JoinMagicForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}